// src/pages/ToolsPage.js - WITH DYNAMIC CATEGORIES AND TYPE FILTER
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import { ToolCardSkeleton } from '../components/SkeletonLoader';
import api from '../services/api';
import '../styles/pages/ToolsPage.css';

const ToolsPage = () => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [filterType, setFilterType] = useState('All');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [toolCategories, setToolCategories] = useState([]);

  // Pagination state - stores tools by category ID
  const [toolsByCategory, setToolsByCategory] = useState({});

  // Ref for intersection observer
  const observerTarget = useRef(null);

  // Fetch tool categories from database
  const fetchToolCategories = async () => {
    try {
      const response = await api.getToolCategories();
      if (response.success && response.data) {
        setToolCategories(response.data);

        // Initialize toolsByCategory state for all categories + 'all'
        const initialState = {
          all: { tools: [], page: 1, hasMore: true, total: 0 }
        };
        response.data.forEach(cat => {
          initialState[cat.id] = { tools: [], page: 1, hasMore: true, total: 0 };
        });
        setToolsByCategory(initialState);
      }
    } catch (error) {
      console.error('Error fetching tool categories:', error);
    }
  };

  useEffect(() => {
    fetchToolCategories();

    return () => {
      setLoading(true);
    };
  }, [location.pathname]);

  // Fetch initial data for active category
  useEffect(() => {
    if (activeCategory && toolsByCategory[activeCategory]) {
      const categoryData = toolsByCategory[activeCategory];
      if (categoryData.tools.length === 0 && categoryData.hasMore) {
        fetchToolsForCategory(activeCategory, 1);
      }
    }
  }, [activeCategory, toolsByCategory]);

  // Re-fetch when filter type changes
  useEffect(() => {
    if (activeCategory && toolsByCategory[activeCategory]) {
      // Reset current category data and fetch with new filter
      setToolsByCategory(prev => ({
        ...prev,
        [activeCategory]: { tools: [], page: 1, hasMore: true, total: 0 }
      }));
      setTimeout(() => fetchToolsForCategory(activeCategory, 1), 100);
    }
  }, [filterType]);

  // Re-fetch when search query changes (with debounce)
  useEffect(() => {
    if (!activeCategory || !toolsByCategory[activeCategory]) return;

    const debounceTimer = setTimeout(() => {
      // Reset current category data and fetch with search
      setToolsByCategory(prev => ({
        ...prev,
        [activeCategory]: { tools: [], page: 1, hasMore: true, total: 0 }
      }));
      fetchToolsForCategory(activeCategory, 1);
    }, 500); // 500ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (!activeCategory) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore) {
          const categoryData = toolsByCategory[activeCategory];
          if (categoryData && categoryData.hasMore) {
            loadMoreTools();
          }
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [activeCategory, loading, loadingMore, toolsByCategory]);

const fetchToolsForCategory = async (categoryId, page) => {
  setLoading(page === 1);
  setLoadingMore(page > 1);
  
  try {
    const params = {
      page: page,
      limit: 6
    };
    
    // Only add category_id if it's not 'all'
    if (categoryId !== 'all') {
      params.category_id = categoryId;
    }
    
    // Only add type filter if it's not 'All'
    if (filterType !== 'All') {
      params.type = filterType;
    }
    
    // Only add search if there's a query
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    console.log('Fetching tools with params:', params); // Debug log
    
    const response = await api.getTools(params);
    
    console.log('Tools API response:', response); // Debug log
    
    if (response.success && response.data) {
      setToolsByCategory(prev => {
        const currentTools = page === 1 ? [] : (prev[categoryId]?.tools || []);
        return {
          ...prev,
          [categoryId]: {
            tools: [...currentTools, ...response.data],
            page: page,
            hasMore: response.pagination.has_more,
            total: response.pagination.total
          }
        };
      });
    }
  } catch (error) {
    console.error('Error fetching tools:', error);
  } finally {
    setLoading(false);
    setLoadingMore(false);
  }
};

  const loadMoreTools = () => {
    if (!activeCategory) return;

    const categoryData = toolsByCategory[activeCategory];
    if (categoryData && categoryData.hasMore && !loadingMore) {
      fetchToolsForCategory(activeCategory, categoryData.page + 1);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);

    // Reset category data when switching
    if (toolsByCategory[categoryId]) {
      setToolsByCategory(prev => ({
        ...prev,
        [categoryId]: { tools: [], page: 1, hasMore: true, total: 0 }
      }));
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();

    // Reset current category data and fetch with search
    if (activeCategory && toolsByCategory[activeCategory]) {
      setToolsByCategory(prev => ({
        ...prev,
        [activeCategory]: { tools: [], page: 1, hasMore: true, total: 0 }
      }));
      setTimeout(() => fetchToolsForCategory(activeCategory, 1), 100);
    }
  };

  const currentTools = activeCategory && toolsByCategory[activeCategory]
    ? toolsByCategory[activeCategory].tools
    : [];

  return (
    <PageWrapper>
      <SEO
        title="Resource Library | Supernova Lab"
        description="Explore our Experimentation Playbook, Readiness Lab frameworks, and Multi-Capital Impact Tracker"
        keywords="tools, resources, experimentation, frameworks, Barbados"
      />

      <main className="tools-page">
        <motion.section
          className="tools-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <h1 className="tools-main-title text-gradient-purple">
              Resource Library
            </h1>
            <p className="tools-subtitle">
              Explore our Experimentation Playbook, Readiness Lab frameworks, and Multi-Capital<br />
              Impact Tracker created to make experimentation faster, safer and more effective.<br />
              Free to use. Build on them. Share What you learn.
            </p>

            <div className="tools-search-wrapper">
              <form onSubmit={handleSearch} style={{ width: '100%' }}>
                <input
                  type="text"
                  className="tools-search-input"
                  placeholder="Search tools and resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="tools-filter-section py-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="container">
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <span className="filter-label">Filter by File type:</span>
                <select
                  className="form-select filter-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{ width: '180px', cursor: 'pointer' }}
                >
                  <option value="All">All Types</option>
                  <option value="Guide">Guide</option>
                  <option value="Template">Template</option>
                  <option value="Course">Course</option>
                  <option value="Tool">Tool</option>
                </select>
              </div>
            </div>

            <div className="category-tabs">
              <button
                className={`category-tab ${activeCategory === 'all' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('all')}
              >
                All Categories
              </button>
              {toolCategories.map(category => (
                <button
                  key={category.id}
                  className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </motion.section>

        <section className="tools-grid-section">
          <div className="container">
            {/* <div className="d-flex justify-content-end mb-4">
              <button className="download-toolkit-btn">
                <span>Download Toolkit</span>
                <i className="fas fa-download ms-2"></i>
              </button>
            </div> */}

            {loading && currentTools.length === 0 ? (
              <div className="row g-1">
                {Array(6).fill(0).map((_, index) => (
                  <div key={index} className="col-lg-4 col-md-6">
                    <ToolCardSkeleton />
                  </div>
                ))}
              </div>
            ) : currentTools.length > 0 ? (
              <>
                <div className="row g-1">
                  {currentTools.map((tool) => (
                    <div key={tool.id} className="col-lg-4 col-md-6">
                      <div className="tool-card">
                        <div
                          className="tool-icon-wrapper"
                          style={{ backgroundColor: tool.icon_bg }}
                        >
                          <i
                            className={`fas ${tool.icon} tool-icon`}
                            style={{ color: tool.icon_color }}
                          ></i>
                        </div>

                        <div className="tool-card-content">
                          <span className="tool-type-badge">{tool.type}</span>
                          <h3 className="tool-title">{tool.title}</h3>
                          <p className="tool-description">
                            {tool.description.length > 100 ? tool.description.substring(0, 80) + '...' : tool.description}
                          </p>
                          <a
                            href={tool.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tool-action-btn"
                            style={{ display: 'inline-block', textDecoration: 'none' }}
                          >
                            {tool.button_text}
                            {tool.button_icon && (
                              <i className={`fas ${tool.button_icon} ms-2`}></i>
                            )}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Loading More Skeleton */}
                {loadingMore && (
                  <div className="row g-1 mt-1">
                    {Array(3).fill(0).map((_, index) => (
                      <div key={`loading-${index}`} className="col-lg-4 col-md-6">
                        <ToolCardSkeleton />
                      </div>
                    ))}
                  </div>
                )}

                {/* Intersection Observer Target */}
                <div ref={observerTarget} style={{ height: '20px' }} />
              </>
            ) : (
              <div className="row">
                <div className="col-12 text-center py-5">
                  <p className="text-muted text-center">No resources available for this filter</p>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
   </PageWrapper>
  );
};

export default ToolsPage;